import { useState, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, AlertCircle, CheckCircle, AlertTriangle, XCircle } from "lucide-react";
import { useToolLifecycle } from "@/utils/trackingHooks";
import LeadForm from "@/forms/engine/LeadForm";
import toolOverpaying from "@/forms/definitions/toolOverpaying";
import { CONTRACT_STATUS_OPTIONS } from "@/forms/definitions/common";

const SCORECARD_ITEM_NAME = "overpaying_scorecard";
const SCORECARD_ITEM_OPTS = { itemType: "calculator" };

const TURNOVER_OPTIONS = [
  { value: "under_10k", label: "Under \u00a310,000" },
  { value: "band_10_30", label: "\u00a310,000\u2013\u00a330,000" },
  { value: "band_30_75", label: "\u00a330,000\u2013\u00a375,000" },
  { value: "over_75", label: "\u00a375,000+" },
];

const AVG_TX_OPTIONS = [
  { value: "under_10", label: "Under \u00a310" },
  { value: "band_10_25", label: "\u00a310\u2013\u00a325" },
  { value: "band_25_50", label: "\u00a325\u2013\u00a350" },
  { value: "over_50", label: "\u00a350+" },
];

const CONTRACT_OPTIONS = [
  { value: "out_of_contract", label: "Out of contract" },
  { value: "less_than_6", label: "Less than 6 months left" },
  { value: "six_to_twelve", label: "6\u201312 months left" },
  { value: "over_twelve", label: "Over 12 months left" },
  { value: "not_sure", label: "Not sure" },
];

const CLARITY_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "somewhat", label: "Somewhat" },
  { value: "no", label: "No" },
];

const RESULT_BULLETS = {
  low_risk: [
    "Your pricing appears broadly competitive based on the figures entered",
    "A review may still uncover hidden fees or better setup options",
    "It is worth checking support, flexibility, and contract position too",
  ],
  could_be_improved: [
    "Some of your current charges may be higher than benchmark levels",
    "A better pricing structure or setup could reduce monthly costs",
    "A tailored review would show whether switching is worthwhile",
  ],
  likely_overpaying: [
    "Your current fee structure appears expensive in one or more key areas",
    "Lower rates or better setup terms could make a meaningful difference",
    "A statement review would help identify the real savings opportunity",
  ],
};

function scoreDebitRate(rate) {
  if (rate <= 0.50) return 0;
  if (rate <= 0.70) return 10;
  if (rate <= 1.00) return 18;
  return 25;
}

function scoreCreditRate(rate) {
  if (rate <= 0.89) return 0;
  if (rate <= 1.20) return 10;
  if (rate <= 1.60) return 18;
  return 25;
}

function scoreAuthFee(fee) {
  if (fee <= 0.03) return 0;
  if (fee <= 0.05) return 8;
  return 15;
}

function scoreTerminalRental(rental, count) {
  const perTerminal = count > 0 ? rental / count : rental;
  if (perTerminal <= 20) return 0;
  if (perTerminal <= 30) return 6;
  if (perTerminal <= 40) return 10;
  return 15;
}

function scorePciFee(fee) {
  if (fee <= 5) return 0;
  if (fee <= 10) return 5;
  return 10;
}

function scoreClarity(val) {
  if (val === "yes") return 0;
  if (val === "somewhat") return 5;
  return 10;
}

function scoreContract(val) {
  if (val === "out_of_contract") return 5;
  if (val === "less_than_6") return 3;
  if (val === "six_to_twelve") return 2;
  if (val === "over_twelve") return 0;
  return 5;
}

function scoreTxProfile(val) {
  if (val === "under_10") return 10;
  if (val === "band_10_25") return 5;
  if (val === "band_25_50") return 2;
  return 0;
}

function scoreTurnover(val) {
  if (val === "under_10k") return 2;
  if (val === "band_10_30") return 5;
  if (val === "band_30_75") return 8;
  return 10;
}

function calculateOverpayingScore(inputs) {
  const raw =
    scoreDebitRate(parseFloat(inputs.debitRate) || 0) +
    scoreCreditRate(parseFloat(inputs.creditRate) || 0) +
    scoreAuthFee(parseFloat(inputs.authFee) || 0) +
    scoreTerminalRental(parseFloat(inputs.terminalRental) || 0, parseInt(inputs.terminalCount, 10) || 1) +
    scorePciFee(parseFloat(inputs.pciFee) || 0) +
    scoreClarity(inputs.pricingClarity) +
    scoreContract(inputs.contractStatus) +
    scoreTxProfile(inputs.averageTransactionBand) +
    scoreTurnover(inputs.monthlyTurnoverBand);

  const score = Math.max(0, Math.min(100, raw));

  if (score >= 65) {
    return { score, category: "likely_overpaying", headline: "You may be overpaying for card payments", description: "Based on your answers, there are strong indicators that your current rates or fees may be higher than they need to be." };
  }
  if (score >= 35) {
    return { score, category: "could_be_improved", headline: "Your payment setup could probably be improved", description: "Based on your answers, there are some signs that your current setup may not be as competitive or efficient as it could be." };
  }
  return { score, category: "low_risk", headline: "Your payment setup looks fairly competitive", description: "Based on the information provided, your current payment setup does not immediately suggest major overpayment. There may still be opportunities to review structure, support, or hidden charges." };
}

const OVERPAYING_CATEGORY_LABEL = {
  low_risk: "Low risk",
  could_be_improved: "Could be improved",
  likely_overpaying: "Likely overpaying",
};

// Turnover is asked as a band; the CRM stores a currency, so the band's
// midpoint is sent to Monthly_Card_Turnovers (labelled as an estimate).
const TURNOVER_MIDPOINT = { under_10k: 5000, band_10_30: 20000, band_30_75: 50000, over_75: 100000 };

const labelOf = (options, value) => options.find((o) => o.value === value)?.label;
const numberOrUndefined = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};
const compact = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ""));

/** Scorecard inputs + result as readable `context` for the Lead (see toolOverpaying.jsx). */
function buildLeadContext(inputs, result) {
  const turnoverLabel = labelOf(TURNOVER_OPTIONS, inputs.monthlyTurnoverBand);
  const midpoint = TURNOVER_MIDPOINT[inputs.monthlyTurnoverBand];
  const terminalCount = parseInt(inputs.terminalCount, 10);
  return compact({
    tool: "Overpaying Scorecard",
    score: result.score,
    category: OVERPAYING_CATEGORY_LABEL[result.category] || result.category,
    monthly_card_turnover: turnoverLabel && midpoint
      ? `${turnoverLabel} (midpoint estimate £${midpoint.toLocaleString("en-GB")})`
      : turnoverLabel,
    monthly_card_turnover_midpoint_gbp: midpoint,
    average_transaction_value: labelOf(AVG_TX_OPTIONS, inputs.averageTransactionBand),
    debit_rate_pct: numberOrUndefined(inputs.debitRate),
    credit_rate_pct: numberOrUndefined(inputs.creditRate),
    auth_fee_gbp: numberOrUndefined(inputs.authFee),
    terminal_rental_gbp_per_month: numberOrUndefined(inputs.terminalRental),
    pci_fee_gbp_per_month: numberOrUndefined(inputs.pciFee),
    terminal_count: Number.isFinite(terminalCount) && terminalCount > 0 ? terminalCount : undefined,
    contract_status: labelOf(CONTRACT_STATUS_OPTIONS, inputs.contractStatus),
    pricing_clarity: labelOf(CLARITY_OPTIONS, inputs.pricingClarity),
  });
}

function SelectInput({ label, name, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-brand-black mb-1.5">{label}</label>
      <select name={name} value={value} onChange={onChange} className="w-full px-4 py-3 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all appearance-none" data-testid={testId}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function NumberField({ label, name, value, onChange, placeholder, helper, testId, prefix }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-brand-black mb-1">{label}</label>
      {helper && <p className="text-xs text-brand-grey mb-1.5">{helper}</p>}
      <div className="relative">
        {prefix && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-grey text-sm">{prefix}</span>}
        <input type="number" name={name} value={value} onChange={onChange} placeholder={placeholder} step="any" min="0" className={`w-full ${prefix ? "pl-8" : "pl-4"} pr-4 py-3 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all`} data-testid={testId} />
      </div>
    </div>
  );
}

function ScoreMeter({ score, category }) {
  const color = { low_risk: "text-emerald-600", could_be_improved: "text-amber-500", likely_overpaying: "text-red-500" };
  const bg = { low_risk: "bg-emerald-500", could_be_improved: "bg-amber-500", likely_overpaying: "bg-red-500" };
  const label = { low_risk: "Low risk", could_be_improved: "Could be improved", likely_overpaying: "Likely overpaying" };
  const pct = Math.min(score, 100);

  return (
    <div className="text-center result-card-animate" data-testid="score-meter">
      <p className={`text-5xl font-heading font-bold ${color[category]}`} data-testid="score-value">{score}</p>
      <p className="text-sm text-brand-grey mt-1">out of 100</p>
      <div className="mt-4 h-3 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ease-out ${bg[category]}`} style={{ width: `${pct}%` }} />
      </div>
      <p className={`text-sm font-bold mt-2 ${color[category]}`} data-testid="score-category-label">{label[category]}</p>
    </div>
  );
}

function ResultDisplay({ result }) {
  const icon = { low_risk: CheckCircle, could_be_improved: AlertTriangle, likely_overpaying: XCircle };
  const iconColor = { low_risk: "text-emerald-500", could_be_improved: "text-amber-500", likely_overpaying: "text-red-500" };
  const Icon = icon[result.category];
  const bullets = RESULT_BULLETS[result.category];

  return (
    <div className="space-y-5" data-testid="scorecard-result">
      <ScoreMeter score={result.score} category={result.category} />
      <div className="flex items-center gap-2 justify-center">
        <Icon size={20} className={iconColor[result.category]} />
        <h3 className="font-heading text-xl font-bold text-brand-black" data-testid="scorecard-headline">{result.headline}</h3>
      </div>
      <p className="text-sm text-brand-grey text-center leading-relaxed">{result.description}</p>
      <div className="space-y-2 max-w-md mx-auto">
        {bullets.map((b) => (
          <div key={b} className="flex items-start gap-2.5 text-left">
            <CheckCircle size={15} className="text-brand-aqua flex-shrink-0 mt-0.5" />
            <span className="text-sm text-brand-black">{b}</span>
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-3 pt-1">
        <Link to="/switch-and-save" className="flex-1 bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 transition-colors btn-shine" data-testid="scorecard-cta-review">Get my savings review <ArrowRight size={16} /></Link>
        <a href="/book" className="flex-1 bg-white border border-brand-aqua text-brand-aqua-dark font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-brand-aqua-light transition-colors" data-testid="scorecard-cta-call">Book a quick call</a>
      </div>
    </div>
  );
}

function useScorecard() {
  const [inputs, setInputs] = useState({
    monthlyTurnoverBand: "band_10_30", averageTransactionBand: "band_10_25",
    debitRate: "", creditRate: "", authFee: "", terminalRental: "", pciFee: "",
    contractStatus: "not_sure", terminalCount: "1", pricingClarity: "somewhat",
  });
  const [showResult, setShowResult] = useState(false);
  const { markStarted, markCompleted } = useToolLifecycle(SCORECARD_ITEM_NAME, SCORECARD_ITEM_OPTS);

  const handleChange = useCallback((e) => {
    markStarted();
    const { name, value } = e.target;
    setInputs((p) => ({ ...p, [name]: value }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const result = useMemo(() => {
    if (!showResult) return null;
    return calculateOverpayingScore(inputs);
  }, [showResult, inputs]);

  useEffect(() => { if (result) markCompleted(); }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit = inputs.debitRate !== "" || inputs.creditRate !== "";

  const handleSubmit = useCallback(() => setShowResult(true), []);
  const handleReset = useCallback(() => setShowResult(false), []);

  return { inputs, handleChange, showResult, result, canSubmit, handleSubmit, handleReset };
}

function ScorecardInputs({ inputs, handleChange }) {
  return (
    <>
      <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua">Your business</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SelectInput label="Monthly card turnover" name="monthlyTurnoverBand" value={inputs.monthlyTurnoverBand} onChange={handleChange} options={TURNOVER_OPTIONS} testId="sc-turnover" />
        <SelectInput label="Average transaction value" name="averageTransactionBand" value={inputs.averageTransactionBand} onChange={handleChange} options={AVG_TX_OPTIONS} testId="sc-avg-tx" />
      </div>
      <div className="border-t border-brand-light-grey/50 pt-4" />
      <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua">Your rates & fees</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumberField label="Current debit card rate" name="debitRate" value={inputs.debitRate} onChange={handleChange} placeholder="e.g. 0.8" helper="Enter as a percentage" testId="sc-debit" />
        <NumberField label="Current credit card rate" name="creditRate" value={inputs.creditRate} onChange={handleChange} placeholder="e.g. 1.6" helper="Enter as a percentage" testId="sc-credit" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <NumberField label="Authorisation fee" name="authFee" value={inputs.authFee} onChange={handleChange} placeholder="e.g. 0.05" helper="Per transaction (£)" prefix="£" testId="sc-auth" />
        <NumberField label="Monthly terminal rental" name="terminalRental" value={inputs.terminalRental} onChange={handleChange} placeholder="e.g. 25" prefix="£" testId="sc-terminal" />
        <NumberField label="Monthly PCI fee" name="pciFee" value={inputs.pciFee} onChange={handleChange} placeholder="e.g. 10" prefix="£" testId="sc-pci" />
      </div>
      <div className="border-t border-brand-light-grey/50 pt-4" />
      <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua">Your situation</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SelectInput label="Contract status" name="contractStatus" value={inputs.contractStatus} onChange={handleChange} options={CONTRACT_OPTIONS} testId="sc-contract" />
        <NumberField label="Number of terminals" name="terminalCount" value={inputs.terminalCount} onChange={handleChange} placeholder="1" testId="sc-terminals" />
        <SelectInput label="Do you know your rates clearly?" name="pricingClarity" value={inputs.pricingClarity} onChange={handleChange} options={CLARITY_OPTIONS} testId="sc-clarity" />
      </div>
    </>
  );
}

export default function OverpayingScorecard({ className = "" }) {
  const { inputs, handleChange, showResult, result, canSubmit, handleSubmit, handleReset } = useScorecard();
  const leadContext = useMemo(() => (result ? buildLeadContext(inputs, result) : null), [inputs, result]);

  return (
    <div className={`bg-white border border-brand-light-grey rounded-2xl shadow-sm overflow-hidden ${className}`} data-testid="overpaying-scorecard">
      <div className="bg-brand-aqua-light px-6 sm:px-8 py-5 flex items-center gap-3 border-b border-brand-aqua/10">
        <div className="w-10 h-10 rounded-xl bg-brand-aqua flex items-center justify-center"><AlertTriangle size={20} className="text-white" /></div>
        <div>
          <h3 className="font-heading text-lg font-bold text-brand-black">Card Payment Overpaying Scorecard</h3>
          <p className="text-xs text-brand-grey">Check if you could be overpaying for card payments</p>
        </div>
      </div>

      <div className="p-6 sm:p-8 space-y-5">
        {!showResult && (
          <>
            <ScorecardInputs inputs={inputs} handleChange={handleChange} />
            {!canSubmit && (
              <div className="flex items-start gap-2 text-xs text-brand-grey bg-gray-50 rounded-xl p-3">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                Enter at least one card rate to generate your score.
              </div>
            )}
            <button onClick={handleSubmit} disabled={!canSubmit} className={`w-full font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 btn-form-ready ${canSubmit ? "is-ready" : ""} text-white`} data-testid="sc-submit">
              See my score <ArrowRight size={16} />
            </button>
          </>
        )}

        {showResult && result && (
          <>
            <ResultDisplay result={result} />
            <div className="border-t border-brand-light-grey/60 pt-6">
              <h4 className="font-heading text-lg font-bold text-brand-black text-center mb-1">Get your personalised review</h4>
              <p className="text-xs text-brand-grey text-center mb-4">Tell us where to send your tailored follow-up.</p>
              <LeadForm schema={toolOverpaying} context={leadContext} variant="compact" />
            </div>
            <div className="text-center">
              <button onClick={handleReset} className="text-xs text-brand-grey hover:text-brand-aqua transition-colors underline underline-offset-2" data-testid="sc-restart">Start again</button>
            </div>
          </>
        )}

        <p className="text-[11px] text-brand-grey leading-relaxed text-center">
          This scorecard provides an illustration based on the information entered and general benchmark assumptions. It is not a formal audit or guaranteed savings calculation. A full review may reveal additional fees, pricing details, or contractual factors.
        </p>
      </div>
    </div>
  );
}
